<template>
  <div class="creation-demand-step-clothing">
    <h2 class="step-title-optional">
      {{ $filters.whitespacedPunctuation($t("ETAB.PAGES.DEMANDS.CREATION.@CLOTHING_TITLE")) }}
    </h2>

    <ExtraTipbox v-if="shouldShowAutoselectTips" class="margin-15 font-090">
      <span>{{ $t("ETAB.PAGES.DEMANDS.CREATION.@CLOTHING_AUTOSELECT_TIPS") }}</span>
    </ExtraTipbox>
    <div class="creation-clothing-selection">
      <section>
        <QBtn
          v-for="outfit in availableOutfits"
          :key="outfit.getId()"
          :class="{ active: clothing.isOutfitSelected(outfit.getId()) }"
          no-caps
          :active="clothing.isOutfitSelected(outfit.getId())"
          text-color="accent"
          @click="selectOutfit(outfit)"
        >
          <img :src="outfit.getImg()" :draggable="false" />
          <p :class="{ active: clothing.isOutfitSelected(outfit.getId()) }">{{ outfit.getLabel() }}</p>
          <span
            v-if="clothing.getOutfitColorCode(outfit)"
            class="clothing-color-badge"
            :style="{ background: clothing.getOutfitColorCode(outfit) }"
          />
        </QBtn>
      </section>

      <section>
        <IosCheckbox
          :model-value="clothing.isProvided()"
          :label="$t('ETAB.PAGES.DEMANDS.CREATION.@CLOTHING_PROVIDED')"
          :left-label="true"
          @update:model-value="setProvided($event)"
        />
      </section>

      <section class="creation-clothing-comment">
        <QInput
          v-model.trim="comment"
          filled
          stack-label
          :label="$t('ETAB.PAGES.DEMANDS.CREATION.@CLOTHING_COMMENT_LABEL')"
          :maxlength="MEDIUM_TEXT_LENGTH_LIMIT"
          type="textarea"
          counter
          :placeholder="$t('ETAB.PAGES.DEMANDS.CREATION.@CLOTHING_COMMENT_PLACEHOLDER')"
        />
      </section>
    </div>
    <transition appear enter-active-class="animated fadeIn slower delay-1s" leave-active-class="animated fadeOut">
      <section class="q-footer q-layout__section--marginal fixed-bottom">
        <div>
          <QBtn
            color="secondary"
            class="full-width btn-navigation"
            :label="$t('ETAB.BUTTONS.@NEXT')"
            no-caps
            :disabled="!isCommentValid()"
            :loading="loading"
            @click.once="save"
          />
        </div>
      </section>
    </transition>

    <ClothingColorModal v-model:opened="modalOpened" :colors="availableColors" @select-color="selectColor" />
  </div>
</template>

<script>
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { ClothingModel, IClothing, OutfitColorModel, OutfitModel } from "@core/models";
  import { MEDIUM_TEXT_LENGTH_LIMIT } from "@core/constants";

  import ClothingColorModal from "@etab/components/creation/ClothingColor.modal.vue";

  export default {
    components: {
      ClothingColorModal,
    },
    props: {
      stepData: {
        type: Object,
        default: () => ({}),
      },
      loading: {
        type: Boolean,
        default: false,
      },
      stepAssets: {
        type: Object,
        default: () => ({}),
      },
    },
    emits: ["saveStep"],
    data() {
      return {
        availableOutfits: [],
        clothing: new ClothingModel(),
        modalOpened: false,
        availableColors: [],
        selectedOutfit: undefined,
        shouldShowAutoselectTips: false,
      };
    },
    computed: {
      comment: {
        get() {
          return this.clothing.getComment() || "";
        },
        set(value) {
          return this.clothing.setComment(value);
        },
      },
    },
    created() {
      this.MEDIUM_TEXT_LENGTH_LIMIT = MEDIUM_TEXT_LENGTH_LIMIT;
      this.getAvailableOutfits();
      if (Object.keys(this.stepData).length) {
        this.clothing = new ClothingModel(this.stepData);
      }
      ScrollManager.scrollToTop();
    },

    methods: {
      openModal() {
        this.modalOpened = true;
      },
      closeModal() {
        this.selectedOutfit = undefined;
        this.modalOpened = false;
      },
      getAvailableOutfits() {
        this.availableOutfits = this.stepAssets.clothing.map((outfit) => new OutfitModel(outfit));
        if (!this.clothing.isSpecified()) {
          this.autoselectOutfits(this.availableOutfits);
        }
      },
      selectOutfit(outfit) {
        if (this.clothing.isOutfitSelected(outfit.getId())) {
          this.clothing.unselectOutfit(outfit);
        } else if (outfit.isColorable()) {
          // store the selectedOutfit for later reference
          this.selectedOutfit = outfit;
          this.availableColors = [...outfit.getAvailableColors()].map((color) => new OutfitColorModel(color));
          this.openModal();
        } else {
          this.clothing.selectOutfit(outfit);
        }
      },
      selectColor(color) {
        // set the color on the previously selected outfit
        if (this.selectedOutfit && color) {
          this.clothing.selectOutfit(this.selectedOutfit, color);
          this.closeModal();
        }
      },
      autoselectOutfits(availableOutfits) {
        const selectedOutfit = availableOutfits.filter((outfit) => outfit.selected === true);
        // we show tips only if there's at least one selected outfit
        this.shouldShowAutoselectTips = selectedOutfit.length > 0;
        for (let i = 0; i < selectedOutfit.length; i++) {
          const outfit = selectedOutfit[i];
          const color = outfit.isColorable() && outfit.getAvailableColors().find((c) => c.isAutoSelected());
          this.clothing.selectOutfit(outfit, color);
        }
      },
      setProvided(value) {
        this.clothing.setProvided(value);
      },
      setComment(value) {
        this.clothing.setComment(value);
      },
      isCommentValid() {
        const comment = this.clothing.getComment();
        return !comment || comment.length <= MEDIUM_TEXT_LENGTH_LIMIT;
      },
      save() {
        if (this.isCommentValid()) {
          this.clothing.setSpecified(true);
          this.$emit("saveStep", {
            [IClothing.comment]: this.clothing.getComment(),
            [IClothing.clothing]: this.clothing.getSelectedOutfits(),
            [IClothing.provided]: this.clothing.isProvided(),
          });
        }
      },
    },
  };
</script>

<style lang="scss">
  .creation-demand-step-clothing {
    margin-bottom: 80px;
    .creation-clothing-selection {
      & {
        text-align: center;
      }

      > section {
        margin-bottom: 25px;
      }

      .q-btn {
        & {
          position: relative;
          margin: 5px;
          background: $extra-btn-square-bg;
        }

        &.active {
          border: 2px solid $primary; // quasar override
          background-color: #{$accent}0D;
          .q-btn__content > *:not(i) {
            font-family: $common-lighter-font-family;
            font-weight: normal;
          }
        }

        &.q-btn-item {
          width: 140px;
          height: 140px;
        }

        .q-btn__content {
          flex-direction: column; // two lines

          > img {
            max-width: 60px;
            max-height: 60px;
            margin: 10px;
          }

          > p {
            & {
              margin: 0;
              padding: 0;
              font-family: $common-lighter-font-family;
              color: $primary;
              display: flex;
              justify-content: center;
              flex-direction: column;
              height: 2em; // always prepare for 2 lines
              font-size: 1.25em;
              line-height: 1.15em;
            }
          }
          &.active {
            font-weight: bold;
          }
        }

        .clothing-color-badge {
          width: 25px;
          height: 25px;
          border-radius: 50%;
          box-shadow: $btn-shadow;
          position: absolute;
          top: 5px;
          right: 5px;
        }
      }

      .creation-clothing-comment {
        text-align: left;
      }

      @media screen and (max-width: 320px) {
        .q-btn {
          margin: 2px;
        }
      }
    }
  }
</style>
